"use client"

import { useCallback } from "react"
import type { ResetPasswordData } from "../types/account-details"
import { ACCOUNT_CONSTANTS } from "../constants/account-details"

export const useResetPasswordValidation = () => {
  const validateResetPassword = useCallback((passwordData: ResetPasswordData): string | null => {
    if (!passwordData.newPassword) {
      return ACCOUNT_CONSTANTS.ERROR_MESSAGES.NEW_PASSWORD_REQUIRED
    }

    if (!passwordData.confirmPassword) {
      return ACCOUNT_CONSTANTS.ERROR_MESSAGES.CONFIRM_PASSWORD_REQUIRED
    }

    if (passwordData.newPassword.length < ACCOUNT_CONSTANTS.VALIDATION.MIN_PASSWORD_LENGTH) {
      return ACCOUNT_CONSTANTS.ERROR_MESSAGES.PASSWORD_TOO_SHORT
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      return ACCOUNT_CONSTANTS.ERROR_MESSAGES.PASSWORDS_DONT_MATCH
    }

    // Check password complexity
    const hasUppercase = /[A-Z]/.test(passwordData.newPassword)
    const hasLowercase = /[a-z]/.test(passwordData.newPassword)
    const hasNumber = /\d/.test(passwordData.newPassword)
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(passwordData.newPassword)

    if (!hasUppercase || !hasLowercase || !hasNumber || !hasSpecialChar) {
      return ACCOUNT_CONSTANTS.ERROR_MESSAGES.INVALID_PASSWORD_FORMAT
    }

    return null
  }, [])

  const getPasswordRequirements = useCallback((password: string) => {
    return {
      minLength: password.length >= ACCOUNT_CONSTANTS.VALIDATION.MIN_PASSWORD_LENGTH,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    }
  }, [])

  const getPasswordStrength = useCallback(
    (password: string) => {
      if (!password) return { level: "none", label: "Enter password", score: 0 }

      const requirements = getPasswordRequirements(password)
      const score = Object.values(requirements).filter(Boolean).length

      if (score < 2) return { level: "weak", label: "Weak", score }
      if (score < 4) return { level: "fair", label: "Fair", score }
      if (score < 5) return { level: "good", label: "Good", score }
      return { level: "strong", label: "Strong", score }
    },
    [getPasswordRequirements],
  )

  return {
    validateResetPassword,
    getPasswordRequirements,
    getPasswordStrength,
  }
}
